/**
 * Activity Log panel (§8) for the Product Catalog Control Center AI chatbot.
 * Shows the CALLING user's own changes at the current location and lets them undo
 * a committed change (§8: undo is a small affordance inside this panel only — never
 * a button on the main chat cards). Backed by the `ProductCatalogChatbot` slice.
 */
import React, { useEffect, useState } from 'react';
import { PCChatbot } from '../hooks/usePCChatbot';
import { Icon, Pill, GhostBtn, PrimaryBtn, FONT, relativeTime, absoluteTime, shortStamp, dayBucket } from './atoms';
import type { PCLogEntry } from '../../../interface/ProductCatalogChatbotInterface';

const TYPE_ICON: Record<string, string> = { availability: 'inventory', visibility: 'visibility', session: 'schedule' };

const StatusBadge: React.FC<{ e: PCLogEntry }> = ({ e }) => {
    let cfg: { c: string; bg: string; icon: string; label: string };
    if (e.state === 'undone') cfg = { c: 'var(--t3)', bg: 'var(--bg)', icon: 'undo', label: 'Undone' };
    else if (e.state === 'auto_reverted') cfg = { c: 'var(--t3)', bg: 'var(--bg)', icon: 'restore', label: 'Auto-reverted' };
    else if (e.syncStatus === 'synced') cfg = { c: 'var(--green)', bg: 'rgba(22,163,74,0.10)', icon: 'cloud_done', label: 'Synced' };
    else if (e.syncStatus === 'partial') cfg = { c: '#B45309', bg: 'rgba(180,83,9,0.12)', icon: 'sync_problem', label: 'Partially synced' };
    else if (e.syncStatus === 'failed') cfg = { c: 'var(--red)', bg: 'rgba(220,38,38,0.10)', icon: 'error', label: 'Failed' };
    else cfg = { c: 'var(--orange)', bg: 'rgba(217,119,6,0.10)', icon: 'cloud_off', label: 'Unsynced' };
    return (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 8px', borderRadius: 999, fontSize: 11, fontWeight: 700, color: cfg.c, background: cfg.bg, fontFamily: FONT }}>
            <Icon name={cfg.icon} size={12} color={cfg.c} />{cfg.label}
        </span>
    );
};

/** A short relative time ("Just now", "5m ago", "2h ago", "3d ago") from an ISO timestamp.
 *  The backend stamps `timeLabel: 'Just now'` at write time and never refreshes it, so it goes
 *  stale — recompute from the authoritative `at` instant on every render instead. */
// (relativeTime now lives in atoms.tsx so the chat history and this panel format
// timestamps identically — see the import above.)

const LogRow: React.FC<{ e: PCLogEntry; chat: PCChatbot }> = ({ e, chat }) => {
    const undoable = e.state === 'committed';
    // Undo is irreversible-ish (it pushes a fresh change to sync), so require an explicit
    // confirm tap rather than firing on the first click — same two-step pattern the thread
    // list uses for delete. Stays entirely inside this panel per the undo-visibility rule.
    const [confirming, setConfirming] = useState(false);

    return (
        <div style={{ padding: '12px 14px', borderBottom: '1px solid var(--divider)', fontFamily: FONT }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                <Icon name={TYPE_ICON[e.type] ?? 'bolt'} size={17} color="var(--t3)" style={{ marginTop: 1 }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--t1)' }}>{e.summary}</div>
                    {e.detail && <div style={{ fontSize: 12, color: 'var(--t2)', marginTop: 1 }}>{e.detail}</div>}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6, flexWrap: 'wrap' }}>
                        {/* Relative label to scan by, plus the real clock time beside it. An
                            activity log is an audit trail, so the precise moment has to be
                            readable outright — a tooltip needs a mouse and is unreachable on
                            touch. Full date+time still available on hover. */}
                        <span title={absoluteTime(e.at)} style={{ fontSize: 10.5, color: 'var(--t3)' }}>
                            {relativeTime(e.at, e.timeLabel)}
                            {shortStamp(e.at) && (
                                <>
                                    <span style={{ opacity: 0.5, margin: '0 4px' }}>·</span>
                                    <span style={{ fontVariantNumeric: 'tabular-nums' }}>{shortStamp(e.at)}</span>
                                </>
                            )}
                        </span>
                        <StatusBadge e={e} />
                        {e.bulkCount ? <Pill tone="info">{e.bulkCount} items</Pill> : null}
                        {e.channels && e.channels.length > 0 && (
                            <span style={{ fontSize: 10.5, color: 'var(--t3)' }}>{(e.channelNames ?? e.channels.map(chat.channelName)).join(', ')}</span>
                        )}
                    </div>
                    {/* `at` stays the time of the original change, so an undone entry needs its own
                        line for WHEN it was undone — otherwise the log only ever shows the time of
                        the change that was reversed, never the reversal. */}
                    {e.state === 'undone' && e.undoneAt && (
                        <div style={{ fontSize: 10.5, color: 'var(--t3)', marginTop: 3, display: 'flex', alignItems: 'center', gap: 4 }}>
                            <Icon name="undo" size={12} color="var(--t3)" />
                            Undone {relativeTime(e.undoneAt, 'recently')}
                        </div>
                    )}
                    {undoable && (
                        confirming ? (
                            <div style={{ marginTop: 9, padding: '9px 11px', borderRadius: 10, background: 'var(--bg)', border: '1px solid var(--card-border)' }}>
                                <div style={{ fontSize: 12, color: 'var(--t1)', fontWeight: 600, marginBottom: 8 }}>
                                    Undo this change? It reverses the update and is itself a change you'll need to sync.
                                </div>
                                <div style={{ display: 'flex', gap: 7 }}>
                                    <PrimaryBtn icon="undo" onClick={() => { setConfirming(false); chat.undo(e.id); }}>Yes, undo</PrimaryBtn>
                                    <GhostBtn small onClick={() => setConfirming(false)}>Cancel</GhostBtn>
                                </div>
                            </div>
                        ) : (
                            <div style={{ display: 'flex', gap: 7, marginTop: 9 }}>
                                <GhostBtn icon="undo" small onClick={() => setConfirming(true)}>Undo</GhostBtn>
                            </div>
                        )
                    )}
                </div>
            </div>
        </div>
    );
};

export const PCActivityLogPanel: React.FC<{ chat: PCChatbot; onClose: () => void }> = ({ chat, onClose }) => {
    const { log, logLoading, undoResult, undoOk, error } = chat.state;
    // A SUCCESSFUL undo routes the user to the chat, where the backend's own "Undone: …"
    // message is the confirmation — so anything left to show here is a rejection.
    const undoRejected = undoOk === false;
    const unsynced = log.filter((l) => l.state === 'committed' && l.syncStatus === 'unsynced').length;

    // Refresh the log when the panel opens — but only once identity is resolved (`chat.ready`),
    // otherwise the fetch goes out with empty x-location-id / x-cc-user-id headers and 400s in
    // live mode, which used to surface as a bare "No changes yet" (indistinguishable from a
    // genuinely empty log). Re-runs if readiness flips true after the panel is already open.
    useEffect(() => {
        if (chat.ready) chat.fetchLog();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [chat.ready]);

    // A fetch that FAILED (vs. an empty log) — the error banner is shown for the whole chat, but
    // inside the panel we also want to explain the empty list + offer a retry rather than
    // implying "you've made no changes." Only treat it as an error state when we truly have
    // nothing to show, so a stale-but-present list isn't hidden behind an error.
    const failed = !!error && log.length === 0 && !logLoading;

    return (
        <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0, background: 'var(--surface)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '14px 16px', borderBottom: '1px solid var(--divider)', flexShrink: 0 }}>
                <Icon name="receipt_long" size={19} color="var(--accent)" />
                <div style={{ fontSize: 14, fontWeight: 800, color: 'var(--t1)', fontFamily: FONT }}>Activity log</div>
                {unsynced > 0 && <Pill tone="warn">{unsynced} unsynced</Pill>}
                <button type="button" onClick={onClose} title="Back to chat" style={{ marginLeft: 'auto', border: 'none', background: 'transparent', cursor: 'pointer', color: 'var(--t3)', display: 'flex' }}>
                    <Icon name="close" size={20} />
                </button>
            </div>

            {undoResult && (
                // Shown ONLY here, inside the activity panel. In practice this is the rejected
                // case (`ok: false`) — the user stays put and reads why the reversal was
                // refused. Dismissed explicitly.
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, padding: '10px 16px', background: undoRejected ? 'rgba(220,38,38,0.08)' : 'rgba(22,163,74,0.08)', borderBottom: '1px solid var(--divider)', flexShrink: 0 }}>
                    <Icon name={undoRejected ? 'error' : 'check_circle'} size={16} color={undoRejected ? 'var(--red)' : 'var(--green)'} style={{ marginTop: 1, flexShrink: 0 }} />
                    <div style={{ flex: 1, fontSize: 12.5, color: 'var(--t1)', fontFamily: FONT, lineHeight: 1.4 }}>{undoResult}</div>
                    <button type="button" onClick={chat.clearUndoResult} title="Dismiss" style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: 'var(--t3)', display: 'flex', flexShrink: 0 }}>
                        <Icon name="close" size={16} />
                    </button>
                </div>
            )}

            <div style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
                {logLoading && log.length === 0 ? (
                    <div style={{ padding: '40px 28px', textAlign: 'center', fontFamily: FONT, color: 'var(--t3)', fontSize: 13 }}>Loading your changes…</div>
                ) : failed ? (
                    <div style={{ padding: '40px 28px', textAlign: 'center', fontFamily: FONT }}>
                        <div style={{ width: 48, height: 48, borderRadius: 14, background: 'rgba(220,38,38,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 14px' }}>
                            <Icon name="cloud_off" size={24} color="var(--red)" />
                        </div>
                        <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--t1)' }}>Couldn't load your activity</div>
                        <div style={{ fontSize: 12.5, color: 'var(--t3)', margin: '6px 0 14px', lineHeight: 1.5 }}>{error}</div>
                        <GhostBtn icon="refresh" small onClick={() => chat.fetchLog()}>Try again</GhostBtn>
                    </div>
                ) : log.length === 0 ? (
                    <div style={{ padding: '40px 28px', textAlign: 'center', fontFamily: FONT }}>
                        <div style={{ width: 48, height: 48, borderRadius: 14, background: 'var(--bg)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 14px' }}>
                            <Icon name="history_edu" size={24} color="var(--t3)" />
                        </div>
                        <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--t1)' }}>No changes yet</div>
                        <div style={{ fontSize: 12.5, color: 'var(--t3)', marginTop: 6, lineHeight: 1.5 }}>
                            Changes you make through the assistant — like marking an item out of stock or hiding it on a channel — appear here, and can be undone.
                        </div>
                    </div>
                ) : (
                    <>
                        {/* Same day grouping the chat history uses — 400+ entries as one flat
                            run is unreadable; headings make "what changed yesterday" scannable. */}
                        {log.map((e, i) => {
                            const bucket = dayBucket(e.at);
                            const prev = i > 0 ? dayBucket(log[i - 1].at) : null;
                            return (
                                <React.Fragment key={e.id}>
                                    {bucket !== prev && (
                                        <div style={{
                                            position: 'sticky', top: 0, zIndex: 1,
                                            padding: '8px 16px 6px', background: 'var(--surface)',
                                            fontSize: 10.5, fontWeight: 700, letterSpacing: 0.5,
                                            textTransform: 'uppercase', color: 'var(--t3)',
                                            borderBottom: '1px solid var(--divider)', fontFamily: FONT,
                                        }}>
                                            {bucket}
                                        </div>
                                    )}
                                    <LogRow e={e} chat={chat} />
                                </React.Fragment>
                            );
                        })}
                        {chat.state.logHasMore && (
                            <button
                                type="button"
                                disabled={logLoading}
                                onClick={() => {
                                    const oldest = log[log.length - 1];
                                    if (oldest?.at) chat.loadOlderLog(oldest.at);
                                }}
                                style={{
                                    width: '100%', padding: '13px 16px', border: 'none',
                                    borderTop: '1px solid var(--divider)', background: 'transparent',
                                    color: 'var(--accent)', fontFamily: FONT, fontSize: 12.5, fontWeight: 600,
                                    cursor: logLoading ? 'default' : 'pointer', opacity: logLoading ? 0.55 : 1,
                                }}
                            >
                                {logLoading ? 'Loading…' : 'Show older activity'}
                            </button>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};
